import { useEffect, useState } from 'react';
import { getSupabaseAnon } from '@/utils/supabaseAnon';
import { createComponentLogger } from '@/utils/logger';
import { deriveYearsOfExperience } from '@/components/users/userProfileExperience';

const log = createComponentLogger('useProfileData', 'profile-public');

export interface GoogleReview {
  publicId: string;
  reviewerDisplayName: string;
  reviewerPhotoUrl?: string;
  starRating: number;
  comment: string;
  createTime: string;
}

export interface ProfileAddress {
  street?: string;
  unit?: string;
  suite?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface CompanyLicense {
  state: string;
  licenseNumber: string;
}

export interface ProfileData {
  firstName: string;
  lastName: string;
  title: string;
  nmls: string;
  stateLicenseNumber?: string;
  phone: string;
  email: string;
  bio: string;
  location: string;
  licenseStates: string[];
  yearsOfExperience?: number;
  industryStartYear?: number;
  socialMedia: {
    facebook?: string;
    linkedin?: string;
    instagram?: string;
    tiktok?: string;
    website?: string;
  };
  facialPhoto?: string;
  bodyPhoto?: string;
  mortgageSpecialties: string[];
  reviews: GoogleReview[];
  reviewsUrl?: string;
  companyInfo: {
    name: string;
    legalName: string;
    nmls: string;
    phone: string;
    email: string;
    address: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    logo?: string;
    website?: string;
    equalHousingLender: boolean;
    disclaimers: string[];
    licenses: CompanyLicense[];
    complianceReady: boolean;
  };
  websiteTemplate?: string;
  slug?: string;
  canonicalSlug?: string;
  applicationLink?: string;
  calendarLink?: string;
  isPartner?: boolean;
}

interface PublicProfileRpc {
  slug?: string;
  canonicalSlug?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  phone?: string;
  nmls?: string;
  stateLicenseNumber?: string;
  licenseStates?: unknown;
  bio?: string;
  mortgageSpecialties?: unknown;
  industryStartYear?: number | null;
  yearsOfExperience?: number | null;
  facialPhoto?: string;
  bodyPhoto?: string;
  websiteTemplate?: string;
  applicationLink?: string;
  calendarLink?: string;
  isPartner?: boolean;
  socialMedia?: Record<string, unknown>;
  address?: ProfileAddress;
  company?: {
    name?: string;
    legalName?: string;
    nmls?: string;
    phone?: string;
    email?: string;
    logo?: string;
    website?: string;
    address?: ProfileAddress;
    equalHousingLender?: boolean;
    disclaimers?: unknown;
    licenses?: unknown;
    complianceReady?: boolean;
  };
}

interface PublicProfileRow {
  first_name?: string;
  last_name?: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile_phone?: string;
  mobile_display_consent?: boolean;
  nmls_number?: string;
  state_license_number?: string;
  license_states?: unknown;
  bio?: string;
  specialties?: unknown;
  mortgage_specialties?: unknown;
  years_of_experience?: number | null;
  mortgage_industry_start_year?: number | null;
  profile_photo_facial?: string;
  profile_photo_body?: string;
  website_slug?: string;
  website_template?: string;
  website?: string;
  linkedin_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  tiktok_url?: string;
  calendar_link?: string;
  application_link?: string;
  branch_address?: ProfileAddress;
  is_partner?: boolean;
}

interface ReviewRpcResult {
  reviews?: GoogleReview[];
  header?: { mapsUri?: string } | null;
}

const PUBLIC_PROFILE_COLUMNS = [
  'first_name',
  'last_name',
  'title',
  'email',
  'phone',
  'mobile_phone',
  'mobile_display_consent',
  'nmls_number',
  'state_license_number',
  'license_states',
  'bio',
  'specialties',
  'mortgage_specialties',
  'years_of_experience',
  'profile_photo_facial',
  'profile_photo_body',
  'website_slug',
  'website_template',
  'website',
  'linkedin_url',
  'facebook_url',
  'instagram_url',
  'tiktok_url',
  'calendar_link',
  'application_link',
  'branch_address',
  'is_partner',
  'mortgage_industry_start_year'
].join(',');

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function companyLicenses(value: unknown): CompanyLicense[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return [];
    }
    const license = item as Record<string, unknown>;
    const state = typeof license.state === 'string' ? license.state.trim() : '';
    const licenseNumber = typeof license.licenseNumber === 'string' ? license.licenseNumber.trim() : '';
    return state && licenseNumber ? [{ state, licenseNumber }] : [];
  });
}

function publicUrl(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

function addressParts(address?: ProfileAddress): ProfileAddress {
  if (!address || typeof address !== 'object') {
    return {};
  }
  return address;
}

function formatAddress(address?: ProfileAddress): string {
  const value = addressParts(address);
  const street = [value.street, value.unit || value.suite].filter(Boolean).join(', ');
  const locality = [value.city, value.state].filter(Boolean).join(', ');
  return [street, locality, value.zip].filter(Boolean).join(' ');
}

function mapRpcProfile(profile: PublicProfileRpc): ProfileData {
  const profileAddress = addressParts(profile.address);
  const companyAddress = addressParts(profile.company?.address);
  const social = profile.socialMedia || {};
  return {
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    title: profile.title || 'Loan Officer',
    nmls: profile.nmls || '',
    stateLicenseNumber: profile.stateLicenseNumber,
    phone: profile.phone || '',
    email: profile.email || '',
    bio: profile.bio || '',
    location: formatAddress(profileAddress),
    licenseStates: stringArray(profile.licenseStates),
    yearsOfExperience: deriveYearsOfExperience(profile.industryStartYear)
      ?? profile.yearsOfExperience
      ?? undefined,
    industryStartYear: profile.industryStartYear ?? undefined,
    socialMedia: {
      facebook: publicUrl(social.facebook),
      linkedin: publicUrl(social.linkedin),
      instagram: publicUrl(social.instagram),
      tiktok: publicUrl(social.tiktok),
      website: publicUrl(social.website)
    },
    facialPhoto: publicUrl(profile.facialPhoto),
    bodyPhoto: publicUrl(profile.bodyPhoto),
    mortgageSpecialties: stringArray(profile.mortgageSpecialties),
    reviews: [],
    companyInfo: {
      name: profile.company?.name || 'Lendwise Mortgage',
      legalName: profile.company?.legalName || '',
      nmls: profile.company?.nmls || '',
      phone: profile.company?.phone || '',
      email: profile.company?.email || '',
      address: formatAddress(companyAddress),
      street: companyAddress.street,
      city: companyAddress.city,
      state: companyAddress.state,
      zip: companyAddress.zip,
      logo: publicUrl(profile.company?.logo),
      website: publicUrl(profile.company?.website),
      equalHousingLender: profile.company?.equalHousingLender === true,
      disclaimers: stringArray(profile.company?.disclaimers),
      licenses: companyLicenses(profile.company?.licenses),
      complianceReady: profile.company?.complianceReady === true
    },
    websiteTemplate: profile.websiteTemplate,
    slug: profile.slug,
    canonicalSlug: profile.canonicalSlug,
    applicationLink: publicUrl(profile.applicationLink),
    calendarLink: publicUrl(profile.calendarLink),
    isPartner: profile.isPartner
  };
}

function mapViewProfile(profile: PublicProfileRow, slug: string): ProfileData {
  const address = addressParts(profile.branch_address);
  return {
    firstName: profile.first_name || '',
    lastName: profile.last_name || '',
    title: profile.title || 'Loan Officer',
    nmls: profile.nmls_number || '',
    stateLicenseNumber: profile.state_license_number,
    phone: profile.phone || (profile.mobile_display_consent ? profile.mobile_phone : '') || '',
    email: profile.email || '',
    bio: profile.bio || '',
    location: formatAddress(address),
    licenseStates: stringArray(profile.license_states),
    yearsOfExperience: deriveYearsOfExperience(
      profile.mortgage_industry_start_year
    ) ?? profile.years_of_experience
      ?? undefined,
    industryStartYear: profile.mortgage_industry_start_year ?? undefined,
    socialMedia: {
      facebook: publicUrl(profile.facebook_url),
      linkedin: publicUrl(profile.linkedin_url),
      instagram: publicUrl(profile.instagram_url),
      tiktok: publicUrl(profile.tiktok_url),
      website: publicUrl(profile.website)
    },
    facialPhoto: publicUrl(profile.profile_photo_facial) || publicUrl(profile.profile_photo_body),
    bodyPhoto: publicUrl(profile.profile_photo_body) || publicUrl(profile.profile_photo_facial),
    mortgageSpecialties: stringArray(profile.mortgage_specialties).length > 0
      ? stringArray(profile.mortgage_specialties)
      : stringArray(profile.specialties),
    reviews: [],
    companyInfo: {
      name: 'Lendwise Mortgage',
      legalName: '',
      nmls: '',
      phone: '',
      email: '',
      address: formatAddress(address),
      street: address.street,
      city: address.city,
      state: address.state,
      zip: address.zip,
      equalHousingLender: false,
      disclaimers: [],
      licenses: [],
      complianceReady: false
    },
    websiteTemplate: profile.website_template,
    slug,
    canonicalSlug: profile.website_slug,
    applicationLink: publicUrl(profile.application_link),
    calendarLink: publicUrl(profile.calendar_link),
    isPartner: profile.is_partner
  };
}

export function useProfileData(slug: string) {
  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchProfile() {
      if (!slug) {
        setError('No profile specified');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      const supabase = getSupabaseAnon();

      try {
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_public_loan_officer_profile', { p_slug: slug });

        let profile: ProfileData | null = null;
        if (!rpcError && rpcData) {
          profile = mapRpcProfile(rpcData as PublicProfileRpc);
          if (
            profile.industryStartYear === undefined
            && profile.yearsOfExperience === undefined
          ) {
            const profileSlug = profile.canonicalSlug || slug;
            const experienceResult = await supabase
              .from('public_profiles')
              .select('years_of_experience,mortgage_industry_start_year')
              .eq('website_slug', profileSlug)
              .maybeSingle();
            let legacyExperience = experienceResult.data as {
              mortgage_industry_start_year?: number | null;
              years_of_experience?: number | null;
            } | null;

            // The frontend and additive migration are approved/deployed
            // separately. Preserve the legacy stored fallback while the live
            // view does not yet expose mortgage_industry_start_year.
            if (experienceResult.error) {
              const legacyResult = await supabase
                .from('public_profiles')
                .select('years_of_experience')
                .eq('website_slug', profileSlug)
                .maybeSingle();
              legacyExperience = legacyResult.data as {
                years_of_experience?: number | null;
              } | null;
            }

            if (legacyExperience) {
              const startYear = legacyExperience.mortgage_industry_start_year as
                | number
                | null
                | undefined;
              profile = {
                ...profile,
                industryStartYear: startYear ?? undefined,
                yearsOfExperience: deriveYearsOfExperience(startYear)
                  ?? (legacyExperience.years_of_experience as
                    | number
                    | null
                    | undefined)
                  ?? undefined
              };
            }
          }
        } else {
          const { data: viewData, error: viewError } = await supabase
            .from('public_profiles')
            .select(PUBLIC_PROFILE_COLUMNS)
            .eq('website_slug', slug)
            .maybeSingle();

          if (viewError || !viewData) {
            throw new Error('Profile not found');
          }
          profile = mapViewProfile(viewData as PublicProfileRow, slug);
        }

        if (!profile.companyInfo.complianceReady) {
          throw new Error('Profile compliance information is unavailable');
        }

        const canonicalSlug = profile.canonicalSlug || slug;
        const { data: reviewData, error: reviewError } = await supabase.rpc(
          'get_public_loan_officer_google_reviews',
          {
            p_lo_slug: canonicalSlug,
            p_site_key: 'lendwise-main',
            p_page_size: 6
          }
        );

        if (!reviewError && reviewData) {
          const result = reviewData as ReviewRpcResult;
          profile.reviews = Array.isArray(result.reviews) ? result.reviews : [];
          profile.reviewsUrl = publicUrl(result.header?.mapsUri);
        }

        void supabase.rpc('increment_public_profile_views', { p_slug: canonicalSlug });

        if (!cancelled) {
          setData(profile);
        }
      } catch (err) {
        log.warn('Public profile could not be loaded', { metadata: { slug } });
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load profile');
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchProfile();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { data, loading, error };
}
